// PATH: src/components/layout/AppShell.tsx
import * as React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/LogoutRounded";
import InsightsIcon from "@mui/icons-material/InsightsRounded";
import MenuBookIcon from "@mui/icons-material/MenuBookRounded";
import AccountTreeIcon from "@mui/icons-material/AccountTreeRounded";
import TuneIcon from "@mui/icons-material/TuneRounded";
import HubIcon from "@mui/icons-material/HubOutlined";
import RuleIcon from "@mui/icons-material/RuleRounded";
import ScienceIcon from "@mui/icons-material/ScienceRounded";
import FlagIcon from "@mui/icons-material/FlagOutlined";
import logo from "../../assets/logo_120x64.png";
import { useAuth } from "../../context/AuthContext";
import { canVisit } from "../../auth/access";
import { appVersion } from "../../config/config";
import { tokens } from "../../theme/theme";

const DRAWER_WIDTH = 236;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  /** Une ligne : ce qu'on y fait (infobulle). */
  hint: string;
}

const NAV: Array<{ group: string; items: NavItem[] }> = [
  {
    group: "Pilotage",
    items: [
      {
        label: "Couverture",
        path: "/coverage",
        icon: <InsightsIcon />,
        hint: "Ce qui est prévu, importé et jouable, programme par programme",
      },
    ],
  },
  {
    group: "Contenus",
    items: [
      {
        label: "Chapitres",
        path: "/chapters",
        icon: <MenuBookIcon />,
        hint: "Importer un chapitre, contrôler ses objectifs et ses faits",
      },
      {
        label: "Quêtes à corriger",
        path: "/quest-review",
        icon: <RuleIcon />,
        hint: "Les quêtes signalées par les élèves ou retirées par un relecteur : valider, corriger, rejeter",
      },
    ],
  },
  {
    group: "Référentiel",
    items: [
      {
        label: "Catalogue",
        path: "/catalog",
        icon: <AccountTreeIcon />,
        hint: "Programmes, étapes, niveaux, filières, matières et offres",
      },
      {
        label: "Réglages Tilmid+",
        path: "/app-configs/tilmid-plus",
        icon: <TuneIcon />,
        hint: "Les agents IA de l'app élève : objectifs, faits, quêtes, compagnon et OCR",
      },
      {
        label: "Fournisseurs",
        path: "/llm-providers",
        icon: <HubIcon />,
        hint: "Les fournisseurs d'IA et leurs clés (gardées par la passerelle), les modèles déclarés et leurs tarifs",
      },
      {
        label: "Essais d'objectifs",
        path: "/objective-trials",
        icon: <FlagIcon />,
        hint: "Essayer un autre modèle ou une consigne pour les objectifs sur 1 à 3 chapitres, voir ce qui serait repris ou perdu, avant d'appliquer",
      },
      {
        label: "Essais d'extraction",
        path: "/trials",
        icon: <ScienceIcon />,
        hint: "Essayer d'autres réglages d'extraction et de relecture des faits sur quelques documents, avant de les appliquer",
      },
    ],
  },
];

const SidebarContent: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const { user, logout, access } = useAuth();
  const navigate = useNavigate();
  const reviewer = access === "reviewer";
  const displayName = (user?.name as string) || (user?.email as string) || (reviewer ? "Relecteur" : "Administrateur");
  // Le relecteur ne voit que les écrans qui lui sont ouverts.
  const nav = NAV.map((g) => ({ ...g, items: g.items.filter((it) => canVisit(access, it.path)) })).filter(
    (g) => g.items.length > 0,
  );

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: tokens.sidebar, color: tokens.sidebarText }}>
      <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box component="img" src={logo} alt="Tilmicode" sx={{ height: 30, width: "auto" }} />
        <Box>
          <Typography sx={{ color: "#fff", fontWeight: 650, fontSize: "0.9375rem", lineHeight: 1.2 }}>
            Contenus
          </Typography>
          <Typography sx={{ color: tokens.sidebarMuted, fontSize: "0.6875rem" }}>
            {reviewer ? "Relecture" : "Administration"}
          </Typography>
        </Box>
      </Box>

      <Box component="nav" sx={{ flex: 1, overflowY: "auto", px: 1.5, pb: 2 }}>
        {nav.map((g) => (
          <Box key={g.group} sx={{ mt: 1.5 }}>
            <Typography variant="overline" sx={{ px: 1.25, color: tokens.sidebarMuted, display: "block" }}>
              {g.group}
            </Typography>
            <List disablePadding>
              {g.items.map((it) => (
                <Tooltip key={it.path} title={it.hint} placement="right">
                  <ListItemButton
                    component={NavLink}
                    to={it.path}
                    onClick={onNavigate}
                    sx={{
                      mb: 0.25,
                      py: 0.75,
                      color: tokens.sidebarText,
                      "& .MuiListItemIcon-root": { color: tokens.sidebarMuted, minWidth: 34 },
                      "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
                      "&.active": {
                        bgcolor: "rgba(255,255,255,0.10)",
                        color: "#fff",
                        "& .MuiListItemIcon-root": { color: "#fff" },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ "& svg": { fontSize: 20 } }}>{it.icon}</ListItemIcon>
                    <ListItemText primary={it.label} slotProps={{ primary: { sx: { fontSize: "0.875rem", fontWeight: 550 } } }} />
                  </ListItemButton>
                </Tooltip>
              ))}
            </List>
          </Box>
        ))}
      </Box>

      <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.08)", px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.12)",
            color: "#fff",
            display: "grid",
            placeItems: "center",
            fontSize: "0.8125rem",
            fontWeight: 650,
            flexShrink: 0,
          }}
        >
          {displayName.slice(0, 1).toUpperCase()}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: "0.8125rem", color: "#fff", fontWeight: 550 }}>
            {displayName}
          </Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: tokens.sidebarMuted }}>v{appVersion}</Typography>
        </Box>
        <Tooltip title="Se déconnecter">
          <IconButton size="small" onClick={() => void handleLogout()} sx={{ color: tokens.sidebarMuted, "&:hover": { color: "#fff" } }}>
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

/** Coque de l'app : menu latéral fixe (tiroir sur mobile) et zone de contenu. */
export const AppShell: React.FC = () => {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up("md"));
  const [open, setOpen] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => setOpen(false), [location.pathname]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      {desktop ? (
        <Drawer
          variant="permanent"
          sx={{ width: DRAWER_WIDTH, flexShrink: 0, "& .MuiDrawer-paper": { width: DRAWER_WIDTH, border: 0 } }}
        >
          <SidebarContent />
        </Drawer>
      ) : (
        <>
          <AppBar position="fixed" elevation={0} sx={{ bgcolor: tokens.sidebar }}>
            <Toolbar variant="dense">
              <IconButton edge="start" color="inherit" onClick={() => setOpen(true)} aria-label="Ouvrir le menu">
                <MenuIcon />
              </IconButton>
              <Box component="img" src={logo} alt="Tilmicode" sx={{ height: 24, ml: 1 }} />
            </Toolbar>
          </AppBar>
          <Drawer
            open={open}
            onClose={() => setOpen(false)}
            sx={{ "& .MuiDrawer-paper": { width: DRAWER_WIDTH, border: 0 } }}
          >
            <SidebarContent onNavigate={() => setOpen(false)} />
          </Drawer>
        </>
      )}

      <Box component="main" sx={{ flex: 1, minWidth: 0, px: { xs: 2, md: 4 }, py: { xs: 2, md: 3.5 }, mt: { xs: 6, md: 0 } }}>
        <Box sx={{ maxWidth: 1480, mx: "auto" }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
